import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  AbstractObjectSettleDialogComponent,
  adjustSettlementDate,
  DocumentSearchService,
  EntityClassSelectorComponent,
  EntityClassSelectorUsage,
  EntityInfoComponent,
  ExtRetentionTriggerSelectorComponent,
  FileTypeSelectorComponent,
  isOwnDocumentObject,
  ObjectClassIconComponent,
  SettlementTabState,
  VisibleClassifiersMode
} from '|shared';
import {EntityClassDto, EntityClassType, FileForm, SettlementMethod, SettlementType} from '|api/commons';
import {
  ApiSettlementService,
  DocumentAllowableObjectClass,
  FileDto,
  FilePrepareCompletionDto,
  SettlementDto
} from '|api/document';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {debounceTime, distinctUntilChanged, map, startWith} from 'rxjs/operators';
import {AlertComponent, AnyComponent, IconComponent, SectionComponent} from '@icz/angular-essentials';
import {
  CheckboxComponent,
  DatePickerComponent,
  FormAutocompleteComponent,
  FormFieldComponent,
  getStartOfTheDay,
  IczFormContainerDirective,
  IczFormControl,
  IczFormGroup,
  IczOption,
  IczValidators
} from '@icz/angular-form-elements';
import {FilterOperator, SortDirection} from '@icz/angular-table';
import {AbstractFileSettleFormComponent} from '../abstract-file-settle-form.component';
import {ReactiveFormsModule} from '@angular/forms';
import {enumToOptions} from '@icz/angular-application-core';

interface DocumentForSettlementData {
  objectClass: DocumentAllowableObjectClass,
  refNumber: Nullable<string>,
  settlementId: Nullable<number>,
}

@Component({
  selector: 'icz-file-settle-form',
  templateUrl: './file-settle-form.component.html',
  styleUrls: ['./file-settle-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SectionComponent, EntityInfoComponent, ReactiveFormsModule, IczFormContainerDirective, FormAutocompleteComponent, DatePickerComponent, IconComponent, ObjectClassIconComponent, FormFieldComponent, CheckboxComponent, FileTypeSelectorComponent, EntityClassSelectorComponent, ExtRetentionTriggerSelectorComponent, AlertComponent]
})
export class FileSettleFormComponent extends AbstractFileSettleFormComponent implements OnInit, OnChanges {

  private documentsSearchService = inject(DocumentSearchService);
  private apiSettlementService = inject(ApiSettlementService);

  @Input()
  showFileInfo = false;
  @Input({required: true})
  loadingContext!: AnyComponent;
  @Input()
  entityClasses: EntityClassDto[] = [];
  @Input({required: true})
  settlementTabState!: SettlementTabState;
  @Output()
  settlementValid = new EventEmitter<boolean>();
  @Output()
  settlementTabPrepareChange = new EventEmitter<SettlementTabState>();

  doCloseFile = true;
  validationMessage: Nullable<string>;

  readonly VisibleClassifiersMode = VisibleClassifiersMode;

  fileSettlementTypeOptions: IczOption[] = enumToOptions('settlementType', SettlementType);
  fileFormOptions = enumToOptions('fileForm', FileForm);

  minSettlementDate: Date = new Date();
  maxSettlementDate = new Date();
  isValidSettlementDate = {
    validationFn: (date: Date) => this.isDateValid(date),
    invalidDateMessage : { message: 'Datum nesmí být starší než datum založení spisu'}
  };

  documentOptions: IczOption<number, DocumentForSettlementData>[] = [];
  settlementDate!: Date;

  static prepareEntityClasses(settlementDetails: FilePrepareCompletionDto, form: IczFormGroup, entityClass: EntityClassDto, file: FileDto) {
    form.get('entityClassValidFrom')!.setValue(entityClass!.validFrom);
    form.get('entityClassValidTo')!.setValue(entityClass!.validTo);

    if (!file.caseFile) {
      form.get('entityClassId')!.setValue(settlementDetails.entityClassId);
    }
  }

  get isSettlementByRecord() {
    return this.form.get('fileSettlementType')!.value === SettlementType.BY_RECORD;
  }

  get isSettlementByOtherMethod() {
    return this.form.get('fileSettlementType')!.value === SettlementType.BY_OTHER_METHOD;
  }

  get showDocumentSelector() {
    const settlementType = this.form.get('fileSettlementType')!.value;
    return settlementType === SettlementType.BY_DOCUMENT || settlementType === SettlementType.BY_ASSIGNMENT;
  }

  private isDateValid(date: Date) {
    if (!(date && this.minSettlementDate)) return false;
    return !(date < this.minSettlementDate || date > this.maxSettlementDate);
  }

  override codebooksLoaded() {
    super.codebooksLoaded();
    this.initFormControlListeners();
    if (this.showDocumentSelector) {
      this.findAvailableDocumentsForSettlement();
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.retentionTriggerYearCtrl.valueChanges.pipe(distinctUntilChanged(), debounceTime(1000), takeUntilDestroyed(this.destroyRef)).subscribe(_ => {
      this.updateDisposalData();
    });

    // todo after ESSL-15271 done, check presence in case file only by <file>.caseFileSectionFileId
    if (this.file?.caseFile) {
      this.form.get('entityClassId')!.disable();
    }

    this.form.valueChanges.pipe(
      debounceTime(250),
      startWith(this.form.value),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(_ => {
      this.settlementValid.emit(this.form.valid);
    });
  }

  ngOnChanges(changes: SimpleChanges<this>) {
    if (changes.file && changes.file.currentValue) {
      this.minSettlementDate = getStartOfTheDay(changes.file.currentValue.auditInfo!.createdAt);
      this.form.get('fileForm')!.setValue(changes.file.currentValue.fileForm);
      this.settlementDate = new Date(this.form.get('settlementDate')!.value);
    }

    if (changes.settlementTabState && changes.settlementTabState.currentValue) {
      this.validationMessage = changes.settlementTabState.currentValue.validationMessage;
    }
  }

  private findAvailableDocumentsForSettlement() {
    this.loadingService.doLoading(
      this.documentsSearchService.findAllDocumentsInFile({
        filter: [{
          fieldName: 'fileId',
          value: this.file.id,
          operator: FilterOperator.equals,
        }],
        sort: [{
          fieldName: 'documentPutInGivenFileAt',
          direction: SortDirection.DESC,
        }]
      }, this.file.id, false),
      this.loadingContext
    ).pipe(
      map(page => page.content.map(entry => ({
        value: entry.id!,
        label: entry.subject!,
        data: {
          objectClass: entry.objectClass as unknown as DocumentAllowableObjectClass,
          refNumber: entry.refNumber,
          settlementId: entry.settlementId
        },
      })))
    ).subscribe(documentOptions => {
      const settlementIds = documentOptions
        .filter(op => !isNil(op.data.settlementId))
        .map(op => op.data.settlementId);
      if (settlementIds.length) {
        this.apiSettlementService.settlementFindSettlementsByIds({
          body: {
            ids: settlementIds as number[],
          }
        }).subscribe(settlements => {
          if (settlements.length) {
            this.documentOptions = documentOptions.filter(op => !this.isInitialSettlementDocument(op.data.settlementId, op.value!, settlements));
          }
        });
      } else {
        this.documentOptions = documentOptions;
      }

      const fileInternalOwnDocuments = this.documentOptions.filter(
        o => isOwnDocumentObject({id: o.value!, objectClass: o.data!.objectClass})
      );

      if (fileInternalOwnDocuments.length > 0) {
        const lastInternalDocumentId = fileInternalOwnDocuments[0].value;
        this.form.get('relatedDocumentId')!.setValue(lastInternalDocumentId);
      }
    });
  }

  private isInitialSettlementDocument(settlementId: Nullable<number>, documentId: number, settlements: SettlementDto[]) {
    if (settlementId) {
      const settlement = settlements.find(s => s.id === settlementId);
      return settlement!.documentId === documentId && settlement!.documentId !== settlement!.relatedDocumentId && settlement!.method === SettlementMethod.INDIVIDUAL && settlement!.type === SettlementType.BY_DOCUMENT;
    } else {
      return false;
    }
  }

  private initFormControlListeners() {
    // it works, but fires requests twice, find the other listener
    this.form.get('settlementDate')!.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(settlementDate => {
      if (settlementDate) {
        this.settlementDate = new Date(settlementDate);

        this.loadingService.doLoading(
          this.apiFileService.filePrepareSettlement({
            dateTime: adjustSettlementDate(settlementDate),
            id: this.file.id,
          }),
          this.loadingContext
        ).subscribe(filePrepare => {
          const settlementDetails = filePrepare as FilePrepareCompletionDto;
          FileSettleFormComponent.prepareEntityClasses(settlementDetails, this.form, filePrepare.classificationScheme!, this.file);
          if (settlementDetails.entityClassId) {
            this.updateRetentionAndDisposalData();
          }
        });
      }
    });

    this.form.get('doCloseFile')!.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(value => {
      this.doCloseFile = Boolean(value);

      if (value) {
        this.form.get('closeDate')!.setValue(new Date().toISOString());
        this.retentionTriggerCheckYearCtrl.enable();
        this.retentionTriggerYearCtrl.enable();
      } else {
        this.retentionTriggerCheckYearCtrl.disable();
        this.retentionTriggerYearCtrl.disable();
      }
    });

    this.form.get('fileSettlementType')!.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(fileSettlementType => {
      const settlingDocumentControl = this.form.get('relatedDocumentId')!;

      if (fileSettlementType === SettlementType.BY_DOCUMENT || fileSettlementType === SettlementType.BY_ASSIGNMENT) {
        settlingDocumentControl.setValidators([IczValidators.required()]);
        this.findAvailableDocumentsForSettlement();
      }
      else {
        settlingDocumentControl.setValidators([]);
      }
      settlingDocumentControl.updateValueAndValidity();

      AbstractObjectSettleDialogComponent.processReasonAndContentFields(
        fileSettlementType,
        this.form.get('reason') as IczFormControl,
        this.form.get('content') as IczFormControl
      );
    });

    this.entityClassIdCtrl.valueChanges.pipe(
      startWith(this.form.get('entityClassId')!.value),
      takeUntilDestroyed(this.destroyRef))
      .subscribe(eClassId => {
        if (eClassId && this.entityClasses.length) {
          const entityClass = this.entityClasses.find(ec => ec.id === eClassId)!;
          this.form.get('entityClassValidFrom')!.setValue(entityClass.validFrom);
          this.form.get('entityClassValidTo')!.setValue(entityClass.validTo);
          if (!this.settlementTabState.prepared) {
            this.settlementTabPrepareChange.emit({prepared: true});
          }
          this.updateRetentionAndDisposalData();
          const selectedDisposalSchedule = this.disposalSchedules.find(ds => ds.id === entityClass.disposalScheduleId)!;
          this.fillRetentionSection(selectedDisposalSchedule);
        }
      });
  }

  protected readonly EntityClassType = EntityClassType;
  protected readonly EntityClassSelectorUsage = EntityClassSelectorUsage;
}
